/** Section 2 — "A Strategic Vision for Building a Global Economic Media Network" (EN PDF). */
export const visionEn = {
  id: 'vision',
  previewCount: 6,
  navLabel: 'Strategic vision',
  eyebrow: 'Strategic vision',
  title: 'A Strategic Vision for Building a Global Economic Media Network',
  subtitle:
    'From a television program to a media ecosystem that drives economic relationships and opens markets.',
  blocks: [
    {
      icon: 'campaign',
      title: 'Introduction',
      wide: true,
      paragraphs: [
        'The world does not need another real estate television program. The world needs a new media model capable of connecting countries, governments, investors, capital, corporations, markets, media, and economic opportunities.',
        'This is the vision behind the HOLOL Global Economic Media Initiative. The concept is not to produce a conventional television program that showcases real estate projects or hosts companies for promotional purposes.',
        'It is to build an international economic media ecosystem that uses the power of media across multiple platforms to open markets, communicate national economic visions, attract investment, build institutional relationships, identify opportunities, and support the development of partnerships and business deals. Television is the primary interface.',
      ],
      flowLabel: 'But the real ecosystem is much broader:',
      flow: [
        'Television',
        'Digital media',
        'Social media',
        'Press',
        'Radio',
        'Events',
        'Business relations',
        'International market access',
      ],
      flowJoin: '+',
    },
    {
      icon: 'lightbulb',
      title: 'The Big Idea',
      paragraphs: [
        'Every country has an economic vision. Saudi Arabia has Vision 2030. Egypt has Vision 2030. Singapore has its own economic and development vision. The United Kingdom has its economic and investment strategies.',
        'Every country seeks to:',
      ],
      bullets: [
        'Attract investment.',
        'Open new markets.',
        'Attract capital.',
        'Develop targeted sectors.',
        'Support national companies.',
        'Attract international investors.',
        'Promote cities and major projects.',
        'Build a strong international economic image.',
      ],
      closing:
        "This is where the initiative comes in: the program and the media ecosystem surrounding it become an international platform for communicating and promoting a country's economic vision. It is not simply media coverage of a country — it is media working for the country's economy.",
    },
    {
      icon: 'public',
      title: 'The Program as a National and International Tool',
      paragraphs: [
        'When the program is launched in a particular country, its content is built around fundamental questions:',
      ],
      bullets: [
        'What does the country want to achieve?',
        'What does it need investment in?',
        'Which markets does it want to reach?',
        'What type of investors is it looking for?',
        'Which sectors does it want to develop?',
        'Which projects require capital?',
        'Which companies does it want to attract into the market?',
        'What can the country offer the global investor?',
      ],
      flowLabel: 'The program therefore becomes a platform for presenting:',
      flow: ['Vision', 'Opportunities', 'Investment', 'Markets', 'Projects', 'Partnerships'],
      flowJoin: '+',
    },
    {
      icon: 'account_tree',
      title: 'The Proposed Model',
      wide: true,
      paragraphs: ['The initiative is built around a simple equation:'],
      flow: [
        'The country defines the vision and the need',
        'Media transforms the vision into a global story',
        'The program reaches investors and corporations',
        'The media network opens markets',
        'Meetings bring the right parties together',
        'Relationships develop into meetings and partnerships',
        'Opportunities develop into investments and deals',
      ],
      closing: 'This is the core concept.',
    },
    {
      icon: 'domain',
      title: 'Not a Conventional Real Estate Program',
      paragraphs: [
        'Although the initiative carries the identity of HOLOL Global Real Estate Club, its content is not limited to real estate. Real estate is one of the most important components of any economy, but it serves as a gateway into the broader economic landscape.',
        'The program can therefore cover:',
      ],
      chips: [
        'Real estate',
        'Investment',
        'Finance',
        'Banking',
        'Industry',
        'Tourism',
        'Technology',
        'Energy',
        'Transportation',
        'Cities',
        'Infrastructure',
        'Sports',
        'Corporations',
        'Financial markets',
        'Major projects',
        'International trade',
      ],
      closing: 'All from the perspective of economic growth & investment.',
    },
    {
      icon: 'travel_explore',
      title: 'The Real Power: The International Dimension',
      paragraphs: [
        'A program operating in one country does not speak only to that country — it speaks to the world. If HOLOL Global Real Estate Club — Saudi Arabia is launched, the program does not have to feature Saudi participants only. It can feature:',
      ],
      bullets: [
        'A British investor.',
        'A Singaporean investment fund.',
        'A Turkish company.',
        'An international bank.',
        'An Egyptian developer.',
        'A global technology company.',
      ],
      closing: [
        'Because the objective is not simply "What is happening in Saudi Arabia?" — it is also "Who can participate in Saudi Arabia?"',
        'In the opposite direction, Saudi companies can appear on HOLOL programs in the United Kingdom, Egypt, Türkiye, Singapore, Europe, Asia, and other markets. In this way, the program becomes a cross-border market access platform.',
      ],
    },
    {
      icon: 'account_balance',
      title: 'The Country Is Not Merely a Guest',
      paragraphs: [
        'Under the proposed model, the country becomes a strategic partner in the economic content. The ecosystem can begin with:',
      ],
      flow: [
        'Head of state or head of government',
        'Ministers and economic authorities',
        'Investment promotion agencies',
        'Financial institutions',
        'Investors',
        'Corporations',
        'International markets',
      ],
      closing: "Each season or group of episodes can be built around the country's economic priorities.",
    },
    {
      icon: 'meeting_room',
      title: 'From Media Dialogue to an Economic Relationship Room',
      paragraphs: [
        'One of the most important elements of the initiative is that the relationship does not end when the episode ends. Closed meetings connected to the program can bring together:',
      ],
      chips: [
        'Government officials',
        'Investors',
        'Investment funds',
        'Banks',
        'Corporations',
        'Developers',
        'International investors',
      ],
      listLabel: 'These meetings can take the form of:',
      bullets: [
        'Closed business meetings.',
        'Investment roundtables.',
        'Economic dialogues.',
        'HOLOL Business Rooms.',
      ],
      closing:
        'The appropriate parts can be covered by the media while maintaining the confidentiality of negotiations and potential deals. The program therefore becomes media that opens the door to economic relationships.',
    },
    {
      icon: 'podcasts',
      title: 'Media Is Not Television Alone',
      wide: true,
      paragraphs: [
        'Television represents the primary interface, but the initiative is designed from the outset to operate as a fully integrated media ecosystem. It includes:',
      ],
      labeled: [
        { label: 'Television', text: 'The flagship program and television broadcast.' },
        { label: 'Digital media', text: 'A digital platform and integrated content ecosystem.' },
        { label: 'Social media', text: 'Official channels for the program, the country, and targeted markets.' },
        { label: 'Press', text: 'Coverage, analysis, and interviews across major newspapers and digital publications.' },
        { label: 'Radio', text: 'Interviews, coverage, and economic programming connected to the initiative.' },
        { label: 'Video & streaming', text: 'Short-form and specialized digital content.' },
        { label: 'Events', text: 'Economic forums and business meetings connected to the program.' },
        { label: 'Business media', text: 'Distribution of content through international economic and business media platforms.' },
      ],
    },
    {
      icon: 'bolt',
      title: 'The Digital Power',
      paragraphs: [
        'Each episode does not end when the broadcast ends. Instead, it can be transformed into dozens and hundreds of media assets, including:',
      ],
      chips: [
        'Interviews',
        'Short-form clips',
        'Statements',
        'Data & statistics',
        'Success stories',
        'Investment opportunities',
        'Reports',
        'Infographics',
        'Short videos',
        'LinkedIn content',
        'YouTube content',
        'TikTok & Instagram content',
        'Press articles',
      ],
      closing: 'The program therefore becomes an economic content engine, not simply an hour of television.',
    },
    {
      icon: 'view_timeline',
      title: 'The Proposed Program Structure',
      paragraphs: [
        'The final format for each country can be developed in cooperation with the broadcaster and agency, but the overall framework can include:',
      ],
      numbered: [
        { label: 'The Vision', text: "The country's vision and economic objectives." },
        { label: 'The Decision Makers', text: 'Heads of state and government, ministers, and senior officials.' },
        { label: 'The Capital', text: 'Banks, funds, and investors.' },
        { label: 'The Market', text: 'Market movements, sectors, and opportunities.' },
        { label: 'The Developers', text: 'Developers and major projects.' },
        { label: 'Global Investors', text: 'Investors from outside the country.' },
        { label: 'Business Leaders', text: 'Corporate leaders and major business figures.' },
        { label: 'The Opportunity', text: 'Real investment opportunities.' },
        { label: 'The Deal Room', text: 'Discussions around potential opportunities and partnerships.' },
        { label: 'The Global Bridge', text: 'Connecting the country with international markets.' },
      ],
    },
    {
      icon: 'tune',
      title: 'Why the Program Differs from One Country to Another',
      paragraphs: [
        'The core format is global, but the local content and execution are adaptable. HOLOL Global Real Estate Club — Saudi Arabia does not have to be an identical version of HOLOL Global Real Estate Club — Egypt, Singapore, or the United Kingdom.',
        'Because every country has its own:',
      ],
      chips: [
        'Vision',
        'Economy',
        'Priorities',
        'Sectors',
        'Audience',
        'Language',
        'Culture',
        'Investment objectives',
      ],
      closing: 'The brand, identity, and core principles, however, remain consistent.',
    },
    {
      icon: 'verified',
      title: 'The Global Brand',
      paragraphs: [
        'The objective is to build one global brand — HOLOL Global Real Estate Club — then adapt it to each market:',
      ],
      bullets: [
        'HOLOL Global Real Estate Club — Egypt.',
        'HOLOL Global Real Estate Club — Saudi Arabia.',
        'HOLOL Global Real Estate Club — United Kingdom.',
        'HOLOL Global Real Estate Club — Singapore.',
        'HOLOL Global Real Estate Club — Türkiye.',
      ],
      closing:
        'And so on. Like successful global formats, the initiative maintains its core identity while adapting locally to the characteristics of each market.',
    },
    {
      type: 'parties',
      icon: 'handshake',
      title: 'Partnership Model',
      intro: 'The initiative is built in each market around three principal parties:',
      items: [
        {
          tag: 'First',
          title: 'HOLOL Global Real Estate Club',
          lead: 'Provides:',
          chips: [
            'The global vision',
            'The format',
            'The brand',
            'International relationships',
            'Access to international markets',
            'International guests & high-level personalities',
            'Relationships with investors & corporations',
            'Cross-border opportunities',
            'Connections between markets',
          ],
        },
        {
          tag: 'Second',
          title: 'Media Partner / Agency',
          lead: 'Provides:',
          chips: [
            'Sponsors',
            'Advertisers',
            'Brand relationships',
            'Sponsorship sales',
            'Brand partnerships',
            'Media sales',
            'Media relations',
            'Channel relationships',
            'Commercial package development',
            'Marketing',
            'Digital promotion',
            'Media campaign management',
            'Commercial & media development and execution',
          ],
          note: 'The objective is for the agency to become a partner in the initiative, not merely a production executor.',
        },
        {
          tag: 'Third',
          title: 'Broadcaster / Media Platform',
          lead: 'May provide:',
          chips: [
            'Broadcasting',
            'Production',
            'Studio facilities',
            'Execution team',
            'Distribution',
            'Media inventory',
            'Promotional support',
            'Digital platforms',
          ],
        },
      ],
      closing:
        'The relationship may vary from one country to another. In some markets, the broadcaster may take responsibility for production; in others, the agency may manage production in cooperation with the broadcaster.',
    },
    {
      icon: 'balance',
      title: 'An Open Commercial Model',
      paragraphs: [
        'The initiative does not impose a fixed percentage on any party from the outset, because the value contributed by each party varies from one market to another. The principle, however, is: whoever creates value participates in value.',
      ],
      labeled: [
        {
          label: 'The agency',
          text: 'Brings sponsors, advertisers, partnerships, and broadcasters — and receives a return proportionate to its role.',
        },
        {
          label: 'The broadcaster',
          text: 'Provides production, broadcasting, and distribution — and receives a return proportionate to its contribution.',
        },
        {
          label: 'HOLOL Global Real Estate Club',
          text: 'Provides intellectual property, the format, international relationships, content, market access, and the global network — and receives a return proportionate to the value it creates.',
        },
      ],
      closing: 'The objective: build a sustainable commercial ecosystem, not simply sell advertising time.',
    },
    {
      icon: 'loyalty',
      title: 'Sponsorship',
      paragraphs: [
        'The target sponsorship market is much broader than real estate companies. It can include:',
      ],
      chips: [
        'Banks',
        'Telecommunications',
        'Automotive',
        'Airlines',
        'Hotels',
        'Technology',
        'Insurance',
        'Energy',
        'Building materials',
        'Furniture',
        'Financial services',
        'Investment companies',
        'Industrial companies',
        'PropTech',
        'Multinational corporations',
        'Major global brands',
      ],
      closing: 'Here, sponsorship evolves from advertising into an economic partnership.',
    },
    {
      icon: 'star',
      title: 'Why Will Sponsorship Be Attractive?',
      paragraphs: ['Because the sponsor is not simply buying an advertisement. It can gain:'],
      chips: [
        'Brand visibility',
        'Executive exposure',
        'Government access',
        'International visibility',
        'Business networking',
        'Market entry',
        'Cross-border exposure',
        'Thought leadership',
      ],
      closing: 'The program therefore becomes a strategic platform for the brand.',
    },
    {
      icon: 'flag',
      title: 'The Program as a National Market Promotion Tool',
      paragraphs: [
        'This is one of the key pillars of the initiative. A country can use the program to promote:',
      ],
      chips: [
        'Its economic vision',
        'New cities',
        'Major projects',
        'Investment zones',
        'Tourism',
        'Industry',
        'Technology',
        'Infrastructure',
        'Investment opportunities',
        'Foreign investment attraction programs',
      ],
      closing:
        'Most importantly, the message does not remain within the country — it reaches targeted markets around the world.',
    },
    {
      icon: 'track_changes',
      title: 'A Practical Model',
      labeled: [
        {
          label: 'Asian investors',
          text: 'If a country wants to attract Asian investors, a complete media strategy can be designed to target Asia.',
        },
        {
          label: 'Gulf capital',
          text: 'If it wants Gulf capital, episodes, meetings, and events can be developed specifically for that objective.',
        },
        {
          label: 'European companies',
          text: 'If it wants European companies, the media network and European markets can be used to reach them.',
        },
      ],
      closing: 'In this way, media becomes a market access tool.',
    },
    {
      icon: 'gavel',
      title: 'Relationship with Governments',
      paragraphs: [
        'The initiative does not deal with governments simply as sources of guests. It can provide them with an integrated platform for communicating with investors and international markets. The process can begin with:',
      ],
      flow: [
        'Government vision',
        'Economic priorities',
        'Investment opportunities',
        'Target investors',
        'Media exposure',
        'Business meetings',
        'Potential partnerships & investment',
      ],
      closing:
        'This is what gives the initiative a fundamentally different value proposition from conventional economic television programs.',
    },
    {
      icon: 'corporate_fare',
      title: 'Relationship with Corporations',
      paragraphs: [
        'Corporations also gain an international platform. A company does not appear merely to say "We have a project." It can speak about:',
      ],
      chips: [
        'Its strategy',
        'Its investments',
        'Its expansion plans',
        'Its requirements',
        'The markets it is targeting',
        'The partnerships it is seeking',
        'The technology it provides',
        'Its future vision',
      ],
      closing: 'The program therefore becomes a platform for corporate market expansion.',
    },
    {
      icon: 'tv',
      title: 'From Screen to Market',
      paragraphs: ['The core equation of the initiative is:'],
      flow: [
        'Screen',
        'Awareness',
        'Relationships',
        'Meetings',
        'Opportunities',
        'Partnerships',
        'Investment',
        'Deals',
      ],
      closing:
        'This is where the true value of the initiative becomes clear. The program is not the destination — the program is the starting point.',
    },
    {
      icon: 'hub',
      title: 'The Global Program Network',
      paragraphs: [
        'The strategic target is 100 programs in 5 years — but the objective is not to produce 100 independent programs.',
        'The objective is to build the HOLOL Global Media Network: a network of local programs connected through one global system. Every program opens a market. Every market opens relationships. Every relationship can open an opportunity. And every opportunity can move into another market.',
      ],
      closing:
        'Expansion therefore becomes the replication of a successful model, rather than reinventing the initiative in every country.',
    },
    {
      icon: 'open_in_full',
      title: 'Expansion Model',
      paragraphs: ['After the successful launch of the first program or group of programs:'],
      flow: [
        'Format',
        'Country',
        'Agency',
        'Channel',
        'Sponsors',
        'Production',
        'Government & business network',
        'International guests',
        'Cross-border opportunities',
      ],
      closing:
        'The model is then replicated — making expansion a matter of replication, rather than reinventing the initiative in every market.',
    },
    {
      icon: 'flag_circle',
      title: 'Five-Year Objective',
      paragraphs: ['The objective is to build a global economic media network comprising:'],
      bullets: [
        '100 programs.',
        'Dozens of broadcasters and media platforms.',
        'Hundreds of sponsors.',
        'Thousands of participating companies and institutions.',
        'An international network of investors.',
        'Relationships with governments and economic authorities.',
        'A continuous flow of opportunities between markets.',
      ],
      closing: 'The brand therefore evolves from the name of a program into a global economic media brand.',
    },
    {
      icon: 'task_alt',
      title: 'Why Is the Initiative Executable?',
      paragraphs: ['Because the fundamental components of the infrastructure already exist:'],
      bullets: [
        'Broadcasters exist.',
        'Digital platforms exist.',
        'Advertising agencies exist.',
        'Sponsors exist.',
        'Corporations exist.',
        'Investors exist.',
        'Governments have clear economic visions.',
        'Media organizations are constantly looking for new content.',
        'Brands are constantly looking for new sponsorship and partnership platforms.',
      ],
      closing:
        'The challenge is not to invent the infrastructure — it is to connect these elements within one integrated model. That is the essence of the initiative.',
    },
    {
      icon: 'diversity_3',
      title: 'The Role of the Media Partner',
      paragraphs: [
        'The required media partner is not simply a production company, nor simply an advertising agency. It is a partner capable of bringing together:',
      ],
      chips: [
        'Media',
        'Advertising',
        'Sponsorship',
        'Brand partnerships',
        'Channel relations',
        'Digital media',
        'Content',
        'Commercial development',
      ],
      closing:
        'And, most importantly, the ability to transform the concept into a scalable commercial venture.',
    },
    {
      icon: 'trending_up',
      title: 'The Partner Opportunity',
      paragraphs: [
        'The partner is not entering a single-program project. It is entering a model that can scale from one country, to multiple countries, to a global network. The success of the first program can therefore lead to:',
      ],
      flow: ['5 programs', '10 programs', '25 programs', '50 programs', '100 programs'],
      closing:
        'This creates a new and sustainable source of revenue and commercial partnerships for the partner itself.',
    },
    {
      icon: 'auto_awesome',
      title: 'What Makes the Initiative Different?',
      paragraphs: [
        'Most programs begin with the question: "What will we show?" This initiative begins with:',
      ],
      flow: [
        'What does the country want to achieve?',
        'Who can help achieve it?',
        'How do we reach them?',
        'How do we build the relationship?',
        'How can the relationship become an opportunity?',
      ],
      closing: 'Media therefore becomes part of the economic process itself.',
    },
    {
      icon: 'visibility',
      title: 'The Final Vision',
      paragraphs: [
        'We are not building a television program. We are building a global economic media platform — a platform that brings together:',
      ],
      flow: [
        'Governments',
        'Investors',
        'Capital',
        'Corporations',
        'Real estate',
        'Media',
        'Markets',
      ],
      flowJoin: '+',
      closing:
        'And uses media as a tool for awareness, influence, market access, relationship building, and investment activation.',
    },
    {
      icon: 'language',
      title: 'The Long-Term Vision',
      paragraphs: [
        'Over the coming years, HOLOL Global Real Estate Club can become a global network of programs, platforms, events, and economic relationships. Each country can have its own program:',
      ],
      chips: [
        'HOLOL — Egypt',
        'HOLOL — Saudi Arabia',
        'HOLOL — United Kingdom',
        'HOLOL — Singapore',
        'HOLOL — Türkiye',
      ],
      closing:
        'The network can then expand across dozens of countries — yet all programs operate under one global brand and one global vision.',
    },
    {
      icon: 'flag',
      title: 'Conclusion',
      wide: true,
      highlight: true,
      paragraphs: [
        'This is not an invitation to produce another television program. It is an invitation to build a new model for global economic media — a model that makes:',
      ],
      labeled: [
        { label: 'Media', text: 'A window into the country.' },
        { label: 'The program', text: 'A bridge between markets.' },
        { label: 'Relationships', text: 'An entry point to business.' },
        { label: 'Content', text: 'A tool for attracting investment.' },
        { label: 'Sponsorship', text: 'An economic partnership.' },
        {
          label: 'The success of one program',
          text: 'Scalable into a global network of 100 programs within five years.',
        },
      ],
    },
  ],
};
